/**
 * Admin Invites Page
 * Simple UI for creating and viewing invite codes
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminService, type InviteCode, type CreateInviteRequest } from '@/services/admin.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Copy, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';

export function AdminInvitesPage() {
  const navigate = useNavigate();
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  // Form state
  const [email, setEmail] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [notes, setNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED' | 'ALL'
  >('PENDING');

  // New invite display state
  const [newInvite, setNewInvite] = useState<InviteCode | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  // Fetch invites
  const { data: invites, isLoading: isLoadingInvites } = useQuery({
    queryKey: ['admin', 'invites', statusFilter],
    queryFn: async () => {
      if (!idToken) throw new Error('No token');
      return await adminService.listInvites(idToken, {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        limit: 50,
      });
    },
    enabled: !!idToken,
  });

  // Create invite mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateInviteRequest) => {
      if (!idToken) throw new Error('No token');
      return await adminService.createInvite(idToken, data);
    },
    onSuccess: (data) => {
      // Show the new invite
      setNewInvite(data);
      // Clear form
      setEmail('');
      setNotes('');
      setExpiresInDays('30');
      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      email: email.trim() || undefined,
      expiresInDays: parseInt(expiresInDays, 10),
      maxUses: 1,
      notes: notes.trim() || undefined,
    });
  };

  const copyToClipboard = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-blue-500';
      case 'USED':
        return 'bg-green-500';
      case 'EXPIRED':
        return 'bg-gray-500';
      case 'REVOKED':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            aria-label="Back to home"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Invite Code Management</h1>
        </div>
        <p className="ml-12 text-muted-foreground">
          Create and manage registration invite codes for family members
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Create Invite Form */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Create New Invite Code</CardTitle>
              <CardDescription>Generate a new invite code for registration</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank for a general invite, or restrict to a specific email
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expiresInDays">Expires In (Days)</Label>
                  <Input
                    id="expiresInDays"
                    type="number"
                    min="1"
                    max="365"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="For Uncle Bob"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Help you remember who this invite is for
                  </p>
                </div>

                {createMutation.isError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {createMutation.error instanceof Error
                        ? createMutation.error.message
                        : 'Failed to create invite code'}
                    </AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Generate Invite Code'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* New Invite Display */}
          {newInvite && (
            <Card className="mt-4 border-2 border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Invite Code Created!
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Invite Code</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      value={newInvite.code}
                      readOnly
                      className="text-center font-mono text-2xl font-bold tracking-widest"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(newInvite.code)}
                    >
                      {copiedCode ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Expires</Label>
                    <p>{formatDate(newInvite.expiresAt)}</p>
                  </div>
                  {newInvite.email && (
                    <div>
                      <Label className="text-muted-foreground">For Email</Label>
                      <p className="truncate">{newInvite.email}</p>
                    </div>
                  )}
                  {newInvite.notes && (
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Notes</Label>
                      <p>{newInvite.notes}</p>
                    </div>
                  )}
                </div>

                <Alert>
                  <AlertDescription>
                    Share this code with the recipient. They'll need to enter it during
                    registration.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Invites List */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Invite Codes</CardTitle>
              <CardDescription>View and manage existing invite codes</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filter */}
              <div className="mb-4 flex gap-2">
                {(['ALL', 'PENDING', 'USED', 'EXPIRED', 'REVOKED'] as const).map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </Button>
                ))}
              </div>

              {/* List */}
              {isLoadingInvites ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : invites?.items.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No {statusFilter.toLowerCase()} invite codes found
                </div>
              ) : (
                <div className="max-h-[600px] space-y-3 overflow-y-auto">
                  {invites?.items.map((invite) => (
                    <div
                      key={invite.inviteId}
                      className="space-y-2 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lg font-bold">{invite.code}</span>
                          <Badge className={getStatusColor(invite.status)}>{invite.status}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(invite.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="space-y-1 text-sm">
                        {invite.email && (
                          <p className="text-muted-foreground">
                            <span className="font-medium">For:</span> {invite.email}
                          </p>
                        )}
                        {invite.notes && (
                          <p className="text-muted-foreground">
                            <span className="font-medium">Notes:</span> {invite.notes}
                          </p>
                        )}
                        <p className="text-muted-foreground">
                          <span className="font-medium">Expires:</span>{' '}
                          {formatDate(invite.expiresAt)}
                        </p>
                        {invite.usedAt && invite.status === 'USED' && (
                          <p className="text-muted-foreground">
                            <span className="font-medium">Used:</span> {formatDate(invite.usedAt)}
                          </p>
                        )}
                        <p className="text-muted-foreground">
                          <span className="font-medium">Uses:</span> {invite.currentUses} /{' '}
                          {invite.maxUses}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
